import React, { useState, useEffect } from "react";
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
import MetaMessagingSection from '@/components/settings/MetaMessagingSection';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from "@/lib/utils";
import api from '@/lib/api';
import usersService, { type User, type CreateUserData, type UpdateUserData } from '@/services/users.service';
import { useSnippets, useCreateSnippet, useUpdateSnippet, useDeleteSnippet } from '@/hooks/useSnippets';
import { type CreateSnippetData, type UpdateSnippetData, type Snippet } from '@/services/snippets.service';
import ticketsService from '@/services/tickets.service';
import authService from '@/services/auth.service';
import { useOrganizations, useCreateOrganization, useUpdateOrganization, useDeleteOrganization, useTestIMAPConnection, useTestOrganizationIMAP } from '@/hooks/useOrganizations';
import { type Organization, type CreateOrganizationData, type UpdateOrganizationData } from '@/services/organizations.service';
import { Check, Instagram, X } from "lucide-react";
import WarehouseSettingsSection from '@/components/WarehouseSettingsSection';
import LanguageSection from '@/components/settings/LanguageSection';
import MobileAppSection from '@/components/settings/MobileAppSection';
import McpSection from '@/components/settings/McpSection';
import VoiceSection from '@/components/settings/VoiceSection';
import WhatsappSection from '@/components/settings/WhatsappSection';
import { OrganizationsSection } from './OrganizationsSection';
import { UserDataTable } from './UserDataTable';
import { SnippetDataTable } from './SnippetDataTable';
import { AutoReplyDataTable } from './AutoReplyDataTable';
import { SLASettings } from './SLASettings';
import AITrainingManagement from './AITrainingManagement';
import ShopIntegrationsSection from './ShopIntegrationsSection';
import PaymentsSection from './PaymentsSection';
import LexofficeSection from './LexofficeSection';
import { SignatureSection } from './SignatureSection';
import autoRepliesService, { type AutoReply, type CreateAutoReplyData } from '@/services/autoReplies.service';
import FrequentContactsSection from './FrequentContactsSection';
import PluginsTab from './PluginsTab';
import { TagsTab } from './TagsTab';
import { DeveloperSection } from './DeveloperSection';
import LicenseSection from './LicenseSection';
import ChatbotSection from './ChatbotSection';
import RichTextEditor from './RichTextEditor';
import useAuthStore from '@/stores/authStore';
import {
  Settings,
  ArrowLeft,
  Users,
  Mail,
  ShoppingCart,
  Key,
  Bell,
  Shield,
  Database,
  Webhook,
  Save,
  TestTube,
  Plus,
  Edit,
  Trash2,
  Copy,
  Eye,
  MessageSquare,
  Hash,
  Bot,
  Server,
  Palette,
  Globe,
  Lock,
  FileText,
  Smartphone,
  Store,
  Warehouse,
  Languages,
  MoreHorizontal,
  Crown,
  Headphones,
  User,
  Zap,
  ChevronRight,
  ChevronDown,
  Search,
  Filter,
  UserPlus,
  Clock,
  Brain,
  FileSignature,
  Puzzle,
  Upload,
  Loader2,
  Paperclip,
  ExternalLink,
  Tag,
  Code,
  Reply,
  Plug,
  PhoneCall,
  MessageCircle,
  LogOut,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import EmailSubscriptionsCard from "./EmailSubscriptionsCard";
import { SettingsSectionHeader } from './settings/SettingsConsole';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Separator } from "./ui/separator";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Checkbox } from "./ui/checkbox";
import { formatDateLong, formatDate as formatDateFn } from '@/lib/format';
import { useTranslation } from 'react-i18next';

interface SettingsPageProps {
  className?: string;
  activeSection?: string;
}

const SNIPPET_PLACEHOLDER_GROUPS = [
  {
    label: 'Ticket',
    items: [
      { placeholder: '#{ticket.number}', label: 'Ticket-Nummer' },
      { placeholder: '#{ticket.title}', label: 'Betreff' },
      { placeholder: '#{ticket.tags}', label: 'Tags' },
      { placeholder: '#{ticket.created_at}', label: 'Erstellt am' },
      { placeholder: '#{ticket.updated_at}', label: 'Aktualisiert am' },
      { placeholder: '#{ticket.state.name}', label: 'Status' },
      { placeholder: '#{ticket.priority.name}', label: 'Priorität' },
    ],
  },
  {
    label: 'Kunde',
    items: [
      { placeholder: '#{ticket.customer.firstname}', label: 'Vorname' },
      { placeholder: '#{ticket.customer.lastname}', label: 'Nachname' },
      { placeholder: '#{ticket.customer.email}', label: 'E-Mail' },
    ],
  },
  {
    label: 'Organisation',
    items: [
      { placeholder: '#{ticket.organization.name}', label: 'Name' },
    ],
  },
  {
    label: 'Besitzer (Assignee)',
    items: [
      { placeholder: '#{ticket.owner.firstname}', label: 'Vorname' },
      { placeholder: '#{ticket.owner.lastname}', label: 'Nachname' },
      { placeholder: '#{ticket.owner.email}', label: 'E-Mail' },
    ],
  },
  {
    label: 'Aktueller Benutzer',
    items: [
      { placeholder: '#{user.firstname}', label: 'Vorname' },
      { placeholder: '#{user.lastname}', label: 'Nachname' },
      { placeholder: '#{user.email}', label: 'E-Mail' },
    ],
  },
  {
    label: 'Bestellung',
    items: [
      { placeholder: '#{order.number}', label: 'Bestellnummer' },
      { placeholder: '#{order.customer.name}', label: 'Kundenname' },
      { placeholder: '#{order.date}', label: 'Bestelldatum' },
      { placeholder: '#{order.total}', label: 'Gesamtbetrag' },
      { placeholder: '#{order.payment_status}', label: 'Zahlungsstatus' },
      { placeholder: '#{order.fulfillment_status}', label: 'Versandstatus' },
      { placeholder: '#{order.shipping_address}', label: 'Lieferadresse' },
      { placeholder: '#{order.tracking_url}', label: 'Tracking-URL' },
    ],
  },
];

const SettingsPage = ({ className = "", activeSection: propActiveSection }: SettingsPageProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser, logout } = useAuthStore();
  const isAdmin = currentUser?.role === 'admin';
  
  // URL'den tab parametresini al
  const getActiveTabFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('tab') || propActiveSection || "users";
  };
  
  const [activeSection, setActiveSection] = useState(getActiveTabFromURL());
  
  // URL değiştiğinde tab'ı güncelle
  useEffect(() => {
    setActiveSection(getActiveTabFromURL());
  }, [location.search]);
  
  // Tab değiştiğinde URL'yi güncelle
  const handleTabChange = (value: string) => {
    setActiveSection(value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', value);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
    }
  };
  const [shopifyConnected, setShopifyConnected] = useState(true);
  const [jtlConnected, setJtlConnected] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [slaAlerts, setSlaAlerts] = useState(true);
  
  // User management states
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<CreateUserData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'agent',
    organizationIds: []
  });
  const [selectAllOrgs, setSelectAllOrgs] = useState(false);
  const [editSelectAllOrgs, setEditSelectAllOrgs] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [roleFilter, setRoleFilter] = useState<string>('alle');

  // Snippet management states
  const [isAddSnippetOpen, setIsAddSnippetOpen] = useState(false);
  const [isEditSnippetOpen, setIsEditSnippetOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<Snippet | null>(null);
  const [newSnippet, setNewSnippet] = useState<CreateSnippetData>({
    name: '',
    shortcut: '',
    content: '',
    description: '',
    category: 'general',
    tags: [],
    isGlobal: false,
    variables: {},
    attachmentIds: []
  });
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<{id: string, filename: string}[]>([]);
  const [editUploadingFiles, setEditUploadingFiles] = useState<File[]>([]);
  const [editUploadedAttachments, setEditUploadedAttachments] = useState<{id: string, filename: string, contentType?: string, downloadUrl?: string, previewUrl?: string}[]>([]);
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState('');
  const [previewFileType, setPreviewFileType] = useState('');
  
  // Password validation function
  const validatePassword = (password: string) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    return {
      isValid: Object.values(requirements).every(Boolean),
      requirements
    };
  };
  
  // Form validation function
  const validateForm = () => {
    const errors: {[key: string]: string} = {};

    if (!newUser.firstName.trim()) {
      errors.firstName = 'Vorname ist erforderlich';
    } else if (newUser.firstName.length < 2) {
      errors.firstName = 'Vorname muss mindestens 2 Zeichen haben';
    }

    if (!newUser.lastName.trim()) {
      errors.lastName = 'Nachname ist erforderlich';
    } else if (newUser.lastName.length < 2) {
      errors.lastName = 'Nachname muss mindestens 2 Zeichen haben';
    }

    if (!newUser.email.trim()) {
      errors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email)) {
      errors.email = 'Ungültige E-Mail-Adresse';
    }

    const passwordValidation = validatePassword(newUser.password);
    if (!passwordValidation.isValid) {
      errors.password = 'Passwort erfüllt nicht alle Anforderungen';
    }

    // Require at least one organization for admin/agent
    if ((newUser.role === 'admin' || newUser.role === 'agent' || newUser.role === 'warehouse') && (!newUser.organizationIds || newUser.organizationIds.length === 0)) {
      errors.organizationIds = 'Mindestens eine Organisation muss ausgewählt werden';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Update form validation on input change
  const handleUserInputChange = (field: keyof CreateUserData, value: string) => {
    setNewUser(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  const queryClient = useQueryClient();
  
  // Fetch users - default to active only
  const [userStatusFilter, setUserStatusFilter] = useState<string>('active');
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users', userStatusFilter],
    queryFn: () => usersService.getAllUsers(1, 50, undefined, userStatusFilter === 'all' ? undefined : userStatusFilter),
    enabled: activeSection === 'users'
  });

  // Fetch snippets
  const { data: snippetsData, isLoading: isLoadingSnippets } = useSnippets({ 
    limit: 100 
  });
  const snippets = snippetsData?.data?.snippets || [];
  const filteredSnippets = snippets.filter(
    (snippet) => snippet.shortcut && snippet.shortcut.trim() !== ""
  );
  const hasGlobalSnippets = filteredSnippets.some((snippet) => snippet.isGlobal);
  const createSnippetMutation = useCreateSnippet();
  const updateSnippetMutation = useUpdateSnippet();
  const deleteSnippetMutation = useDeleteSnippet();
  
  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: usersService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      handleCloseDialog();
      toast.success(t('settings:p2.msgUserCreated', 'Benutzer erfolgreich erstellt'));
    },
    onError: (error: any) => {
      console.error('Failed to create user:', error);
      if (error.response?.data?.code === 'USER_INACTIVE_EXISTS') {
        toast.error(error.response.data.message, { duration: 8000 });
      } else {
        toast.error(error.response?.data?.message || 'Fehler beim Erstellen des Benutzers');
      }
    }
  });

  // Deactivate user mutation (soft-delete)
  const deactivateUserMutation = useMutation({
    mutationFn: (userId: string) => usersService.updateUserStatus(userId, 'inactive'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(t('settings:p2.msgUserDeactivated', 'Benutzer erfolgreich deaktiviert'));
    },
    onError: (error: any) => {
      console.error('Failed to deactivate user:', error);
      toast.error(error.response?.data?.message || 'Fehler beim Deaktivieren des Benutzers');
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: UpdateUserData }) =>
      usersService.updateUser(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsEditUserOpen(false);
      setEditingUser(null);
      toast.success(t('settings:p2.msgUserUpdated', 'Benutzer erfolgreich aktualisiert'));
    },
    onError: (error: any) => {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.message || 'Fehler beim Aktualisieren des Benutzers');
    }
  });
  
  const handleCreateUser = () => {
    if (validateForm()) {
      createUserMutation.mutate(newUser);
    }
  };
  
  // Reset form when dialog closes
  const handleCloseDialog = () => {
    setIsAddUserOpen(false);
    setNewUser({ email: '', password: '', firstName: '', lastName: '', role: 'agent', organizationIds: [] });
    setSelectAllOrgs(false);
    setValidationErrors({});
  };
  
  // ------------------------------------------------------------------
  // Auto-Replies Logic
  // ------------------------------------------------------------------
  // Fetch organizations data
  const { data: organizationsData } = useOrganizations();
  
  const { data: autoRepliesData, isLoading: isLoadingAutoReplies } = useQuery({
    queryKey: ['autoReplies'],
    queryFn: () => autoRepliesService.getAllAutoReplies(),
  });
  const autoReplies = autoRepliesData?.data ?? [];

  const [isAddAutoReplyOpen, setIsAddAutoReplyOpen] = useState(false);
  const [editingAutoReply, setEditingAutoReply] = useState<AutoReply | null>(null);
  const [newAutoReply, setNewAutoReply] = useState<{
    organizationId: string;
    templateHtml: string;
    isActive: boolean;
  }>({
    organizationId: '',
    templateHtml: '',
    isActive: true
  });

  const upsertAutoReplyMutation = useMutation({
    mutationFn: ({ organizationId, data }: { organizationId: string; data: CreateAutoReplyData }) =>
      autoRepliesService.upsertAutoReply(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoReplies'] });
      setIsAddAutoReplyOpen(false);
      setEditingAutoReply(null);
      setNewAutoReply({ organizationId: '', templateHtml: '', isActive: true });
      toast.success(editingAutoReply ? "Auto-Antwort aktualisiert" : "Auto-Antwort erstellt");
    },
    onError: () => {
      toast.error(t('settings:p2.msgAutoReplySaveFailed', 'Fehler beim Speichern der Auto-Antwort'));
    }
  });

  const deleteAutoReplyMutation = useMutation({
    mutationFn: (organizationId: string) => autoRepliesService.deleteAutoReply(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoReplies'] });
      toast.success(t('settings:p2.msgAutoReplyDeleted', 'Auto-Antwort gelöscht'));
    },
    onError: () => {
      toast.error(t('settings:p2.msgAutoReplyDeleteFailed', 'Fehler beim Löschen der Auto-Antwort'));
    }
  });

  const toggleAutoReplyStatusMutation = useMutation({
    mutationFn: ({ organizationId, isActive }: { organizationId: string; isActive: boolean }) =>
      autoRepliesService.toggleAutoReplyStatus(organizationId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoReplies'] });
      toast.success(t('settings:p2.msgStatusUpdated', 'Status aktualisiert'));
    },
    onError: () => {
      toast.error(t('settings:p2.msgStatusUpdateFailed', 'Fehler beim Aktualisieren des Status'));
    }
  });

  const handleSaveAutoReply = () => {
    const orgId = editingAutoReply ? editingAutoReply.organization_id : newAutoReply.organizationId;
    const template = editingAutoReply ? editingAutoReply.template_html : newAutoReply.templateHtml;
    
    if (!orgId || !template) {
      toast.error(t('settings:p2.msgFillRequired', 'Bitte alle Pflichtfelder ausfüllen'));
      return;
    }

    upsertAutoReplyMutation.mutate({
      organizationId: orgId,
      data: {
        templateHtml: template,
      },
    });
  };

  const handleEditAutoReply = (autoReply: AutoReply) => {
    setEditingAutoReply(autoReply);
  };

  const handleDeleteAutoReply = (organizationId: string) => {
    deleteAutoReplyMutation.mutate(organizationId);
  };
  
  const handleToggleAutoReply = (autoReply: AutoReply) => {
    toggleAutoReplyStatusMutation.mutate({
      organizationId: autoReply.organization_id,
      isActive: !autoReply.is_active
    });
  };

  const availableOrgs = organizationsData?.data?.filter(
    (org: Organization) => !autoReplies.some(ar => ar.organization_id === org.id)
  ) || [];

  const getOrgName = (orgId: string) => {
    const org = organizationsData?.data?.find((o: Organization) => o.id === orgId);
    return org?.name || 'Unbekannte Organisation';
  };

  const handleDeactivateUser = (userId: string) => {
    deactivateUserMutation.mutate(userId);
  };

  const handleImpersonate = async (user: User) => {
    try {
      const { user: targetUser } = await authService.impersonate(user.id);
      useAuthStore.getState().startImpersonation(targetUser);
      window.location.href = '/tickets';
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Simulation konnte nicht gestartet werden');
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    // Check if user has all organizations selected
    const allOrgIds = organizationsData?.data?.map((o: any) => o.id) || [];
    const userOrgIds = user.organizationIds || [];
    setEditSelectAllOrgs(allOrgIds.length > 0 && allOrgIds.every(id => userOrgIds.includes(id)));
    setIsEditUserOpen(true);
  };

  const handleUpdateUser = () => {
    if (editingUser) {
      // Validate organization selection for admin/agent
      if ((editingUser.role === 'admin' || editingUser.role === 'agent' || editingUser.role === 'warehouse') && (!editingUser.organizationIds || editingUser.organizationIds.length === 0)) {
        toast.error(t('settings:p2.orgRequired', 'Mindestens eine Organisation muss ausgewählt werden'));
        return;
      }
      const updateData: UpdateUserData = {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        email: editingUser.email,
        role: editingUser.role,
        status: editingUser.status,
        organizationIds: editingUser.organizationIds
      };
      updateUserMutation.mutate({ userId: editingUser.id, userData: updateData });
    }
  };

  // Snippet handlers
  const handleFileUpload = async (files: FileList) => {
    const fileArray = Array.from(files);
    setUploadingFiles(fileArray);

    try {
      const uploadPromises = fileArray.map(file => ticketsService.uploadAttachment(file));
      const uploadResults = await Promise.all(uploadPromises);

      const newAttachments = uploadResults.map(result => ({
        id: result.data.id,
        filename: result.data.filename
      }));

      setUploadedAttachments(prev => [...prev, ...newAttachments]);
      setNewSnippet(prev => ({
        ...prev,
        attachmentIds: [...prev.attachmentIds || [], ...newAttachments.map(att => att.id)]
      }));
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setUploadingFiles([]);
    }
  };

  const removeAttachment = (attachmentId: string) => {
    setUploadedAttachments(prev => prev.filter(att => att.id !== attachmentId));
    setNewSnippet(prev => ({
      ...prev,
      attachmentIds: prev.attachmentIds?.filter(id => id !== attachmentId) || []
    }));
  };

  const handleEditFileUpload = async (files: FileList) => {
    const fileArray = Array.from(files);
    setEditUploadingFiles(fileArray);

    try {
      const uploadPromises = fileArray.map(file => ticketsService.uploadAttachment(file));
      const uploadResults = await Promise.all(uploadPromises);

      const newAttachments = uploadResults.map(result => ({
        id: result.data.id,
        filename: result.data.filename
      }));

      setEditUploadedAttachments(prev => [...prev, ...newAttachments]);
      setEditingSnippet(prev => prev ? {
        ...prev,
        attachmentIds: [...prev.attachmentIds || [], ...newAttachments.map(att => att.id)]
      } : null);
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setEditUploadingFiles([]);
    }
  };

  const removeEditAttachment = (attachmentId: string) => {
    setEditUploadedAttachments(prev => prev.filter(att => att.id !== attachmentId));
    setEditingSnippet(prev => prev ? {
      ...prev,
      attachmentIds: prev.attachmentIds?.filter(id => id !== attachmentId) || []
    } : null);
  };

  const handleCreateSnippet = () => {
    createSnippetMutation.mutate(newSnippet, {
      onSuccess: () => {
        setIsAddSnippetOpen(false);
        setNewSnippet({
          name: '',
          shortcut: '',
          content: '',
          description: '',
          category: 'general',
          tags: [],
          isGlobal: false,
          variables: {},
          attachmentIds: []
        });
        setUploadedAttachments([]);
      }
    });
  };

  const handleEditSnippet = async (snippet: Snippet) => {
    setEditingSnippet(snippet);
    setEditUploadedAttachments([]); // State'i temizle
    setEditUploadingFiles([]); // Yüklenen dosyaları da temizle

    // Gerçek attachment bilgilerini API'den çek
    if (snippet.attachmentIds && snippet.attachmentIds.length > 0) {
      try {
        const attachments = await Promise.all(
          snippet.attachmentIds.map(async (id) => {
            try {
              const response = await api.get(`/attachments/${id}`);

              if (response.data.success) {
                const att = response.data.data;
                return {
                  id: att.id,
                  filename: att.filename,
                  contentType: att.contentType,
                  downloadUrl: att.downloadUrl,
                  previewUrl: att.previewUrl
                };
              }
              console.error(`Failed to fetch attachment ${id}`);
              return null;
            } catch (error) {
              console.error(`Error fetching attachment ${id}:`, error);
              return null;
            }
          })
        );
        setEditUploadedAttachments(attachments.filter(Boolean) as any[]);
      } catch (error) {
        console.error('Error loading attachments:', error);
        setEditUploadedAttachments([]);
      }
    } else {
      setEditUploadedAttachments([]);
    }

    setIsEditSnippetOpen(true);
  };

  const handleUpdateSnippet = () => {
    if (editingSnippet) {
      const updateData: UpdateSnippetData = {
        name: editingSnippet.name,
        shortcut: editingSnippet.shortcut,
        content: editingSnippet.content,
        description: editingSnippet.description,
        category: editingSnippet.category,
        tags: editingSnippet.tags,
        isGlobal: editingSnippet.isGlobal,
        variables: editingSnippet.variables,
        attachmentIds: editingSnippet.attachmentIds || []
      };
      updateSnippetMutation.mutate({ id: editingSnippet.id, data: updateData }, {
        onSuccess: () => {
          setIsEditSnippetOpen(false);
          setEditingSnippet(null);
          setEditUploadedAttachments([]);
        }
      });
    }
  };

  const handleDeleteSnippet = (snippetId: string) => {
    if (confirm(t('settings:p2.msgDeleteSnippet', 'Diesen Textbaustein löschen?'))) {
      deleteSnippetMutation.mutate(snippetId);
    }
  };
  
  const getRoleInfo = (role: string) => {
    switch (role) {
      case 'admin': 
        return { 
          label: t('common:role.admin', 'Administrator'), 
          color: 'bg-[#0f172a]',
          textColor: 'text-white',
          icon: Crown
        };
      case 'agent': 
        return { 
          label: t('common:role.agent', 'Support Agent'), 
          color: 'bg-[#0F99FD]',
          textColor: 'text-white',
          icon: Headphones
        };
      case 'customer': 
        return { 
          label: t('ticket:td.customerLabel', 'Kunde'), 
          color: 'bg-slate-500',
          textColor: 'text-white',
          icon: User
        };
      default: 
        return { 
          label: role, 
          color: 'bg-gray-200', 
          textColor: 'text-gray-800',
          icon: User
        };
    }
  };
  
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'active': 
        return { 
          label: t('common:state.active', 'Aktiv'), 
          color: 'bg-green-100 border-green-200', 
          textColor: 'text-green-800',
          dotColor: 'bg-green-500'
        };
      case 'inactive': 
        return { 
          label: t('common:state.inactive', 'Inaktiv'), 
          color: 'bg-slate-700 text-white border-slate-600',
          textColor: 'text-white',
          dotColor: 'bg-slate-400'
        };
      case 'suspended': 
        return { 
          label: t('common:state.blocked', 'Gesperrt'), 
          color: 'bg-red-100 border-red-200', 
          textColor: 'text-red-800',
          dotColor: 'bg-red-500'
        };
      default: 
        return { 
          label: status, 
          color: 'bg-gray-100 border-gray-200', 
          textColor: 'text-gray-800',
          dotColor: 'bg-gray-500'
        };
    }
  };
  
  // Delegiert an den zentralen, sprachabhängigen Formatter (Signatur unverändert)
  const formatDate = (dateString: string) => formatDateLong(dateString);
  
  const formatLastLogin = (lastLogin: string | null) => {
    if (!lastLogin) return 'Noch nie';
    const date = new Date(lastLogin);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) return `Vor ${diffInHours}h`;
    if (diffInHours < 48) return 'Gestern';
    return formatDate(lastLogin);
  };

  const settingsSections = [
    {
      id: "users",
      title: t('settings:section.users.title', "Benutzerverwaltung"),
      icon: Users,
      description: t('settings:section.users.description', "Benutzer und Rollen verwalten"),
    },
    {
      id: "snippets",
      title: t('settings:section.snippets.title', "Textbausteine"),
      icon: FileText,
      description: t('settings:section.snippets.description', "Wiederverwendbare Texte"),
    },
    {
      id: "organizations",
      title: t('settings:section.organizations.title', "Organisationen"),
      icon: Store,
      description: t('settings:section.organizations.description', "Mandanten und E-Mail-Infrastruktur verwalten"),
    },
    {
      id: "lager",
      title: t('settings:section.lager.title', "Lager"),
      icon: Warehouse,
      description: t('settings:section.lager.description', "Support-Empfänger für Lager-Mitarbeiter"),
    },
    {
      id: "mobile-app",
      title: t('settings:section.mobile-app.title', "Mobile App"),
      icon: Smartphone,
      description: t('settings:section.mobile-app.description', "Mobilgerät per QR-Code mit diesem Desk verbinden"),
    },
    {
      id: "language",
      title: t('settings:section.language.title', "Sprache"),
      icon: Languages,
      description: t('settings:section.language.description', "Anzeigesprache der Oberfläche"),
    },
    {
      id: "sla",
      title: t('settings:section.sla.title', "SLA Management"),
      icon: Clock,
      description: t('settings:section.sla.description', "Service level agreements and response times"),
    },
    {
      id: "ai-training",
      title: t('settings:section.ai-training.title', "KI-Training"),
      icon: Brain,
      description: t('settings:section.ai-training.description', "Trainiere die KI mit benutzerdefinierten Anweisungen"),
    },
    {
      id: "plugins",
      title: t('settings:section.plugins.title', "Plugins"),
      icon: Puzzle,
      description: t('settings:section.plugins.description', "PowerfulForm und andere Integrationen"),
    },
    {
      id: "tags",
      title: t('settings:section.tags.title', "Tags"),
      icon: Tag,
      description: t('settings:section.tags.description', "Ticket-Tags verwalten und organisieren"),
    },
    {
      id: "integrations",
      title: t('settings:section.integrations.title', "Integrationen"),
      icon: Zap,
      description: t('settings:section.integrations.description', "Shopify und JTL-Shop"),
    },
    {
      id: "chatbot",
      title: t('settings:section.chatbot.title', "Chatbot"),
      icon: Bot,
      description: t('settings:section.chatbot.description', "Website-Chatbot Widgets verwalten"),
    },
    {
      id: "signatures",
      title: t('settings:section.signatures.title', "E-Mail-Signaturen"),
      icon: FileSignature,
      description: t('settings:section.signatures.description', "Persönliche Signaturen verwalten"),
    },
    {
      id: "auto-replies",
      title: t('settings:section.auto-replies.title', "Auto-Antworten"),
      icon: Reply,
      description: t('settings:section.auto-replies.description', "Automatische Antworten für neue Tickets"),
    },
    {
      id: "contacts",
      title: t('settings:section.contacts.title', "Häufige Kontakte"),
      icon: Users,
      description: t('settings:section.contacts.description', "Kontaktverzeichnis für schnellen Zugriff"),
    },
    {
      id: "voice",
      title: t('settings:section.voice.title', "Telefonie"),
      icon: PhoneCall,
      description: t('settings:section.voice.description', "Telnyx-Verbindung für Anrufe im Browser"),
    },
    {
      id: "whatsapp",
      title: t('settings:section.whatsapp.title', "WhatsApp"),
      icon: WhatsAppIcon,
      description: t('settings:section.whatsapp.description', "WhatsApp Business — Nachrichten werden zu Tickets"),
    },
    {
      id: "meta-messaging",
      title: t('settings:section.metaMessaging.title', "Messenger & Instagram"),
      icon: Instagram,
      description: t('settings:section.metaMessaging.description', "Facebook- und Instagram-Nachrichten werden zu Tickets"),
    },
    {
      id: "mcp",
      title: t('settings:section.mcp.title', "MCP-Zugriff"),
      icon: Plug,
      description: t('settings:section.mcp.description', "KI-Clients mit VynDesk verbinden"),
    },
    {
      id: "developer",
      title: t('settings:section.developer.title', "Developer"),
      icon: Code,
      description: t('settings:section.developer.description', "API Keys und externe Integrationen"),
    },
    {
      id: "license",
      title: t('settings:section.license.title', "Lizenz"),
      icon: Shield,
      description: t('settings:section.license.description', "Plan, Limits und Lizenzdetails"),
    },
    {
      id: "notification-recipients",
      title: t('settings:section.notification-recipients.title', "Benachrichtigungen"),
      icon: Bell,
      description: t('settings:section.notification-recipients.description', "E-Mail-Empfänger für Benachrichtigungen verwalten"),
    },
  ];
  const activeSectionInfo = settingsSections.find((s) => s.id === activeSection);
  const settingsGroups = [
    {
      label: t('settings:group.team', 'Team & Organisation'),
      ids: ['users', 'organizations', 'lager', 'contacts'],
    },
    {
      label: t('settings:group.workspace', 'Arbeitsbereich'),
      ids: ['snippets', 'signatures', 'auto-replies', 'tags', 'sla', 'language', 'notification-recipients'],
    },
    {
      label: t('settings:group.automation', 'Automatisierung'),
      ids: ['ai-training', 'chatbot', 'voice', 'whatsapp', 'meta-messaging'],
    },
    {
      label: t('settings:group.system', 'System & Verbindungen'),
      ids: ['plugins', 'integrations', 'mcp', 'mobile-app', 'developer', 'license'],
    },
  ].map((group) => ({
    ...group,
    sections: group.ids
      .map((id) => settingsSections.find((section) => section.id === id))
      .filter((section): section is (typeof settingsSections)[number] => Boolean(section)),
  }));
  const activeSectionHeader = activeSectionInfo ?? {
    title: 'Einstellungen',
    description: 'Systemkonfiguration verwalten',
    icon: Settings,
  };
  const currentUserName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') || currentUser?.email || 'VynDesk';

  return (
    <div
      className={`settings-shell min-h-screen ${className}`}
    >
      <div className="flex h-screen min-w-0 flex-col lg:flex-row">
        {/* Settings Sidebar */}
        <aside className="settings-sidebar">
          {/* Header */}
          <div className="settings-sidebar__header">
            <div className="settings-sidebar__brand">
              <div className="settings-sidebar__brand-icon">
                <Settings className="h-4 w-4" />
              </div>
              <div>
                <h1>
                  {t('shell:action.settings', 'Einstellungen')}
                </h1>
                <p>{t('settings:p2.systemConfig', 'Systemkonfiguration')}</p>
              </div>
            </div>
          </div>

          {/* Navigation - Scrollable */}
          <div
            className="settings-sidebar__scroll sidebar-scrollbar"
          >
            <nav className="settings-sidebar__nav">
              {settingsGroups.map((group) => (
                <div className="settings-sidebar__group" key={group.label}>
                  <p className="settings-sidebar__group-label">{group.label}</p>
                  <div className="settings-sidebar__group-items">
                    {group.sections.map((section) => {
                      const Icon = section.icon;
                      const isActive = activeSection === section.id;
                      return (
                        <button
                          key={section.id}
                          onClick={() => handleTabChange(section.id)}
                          className="settings-sidebar__item"
                          data-active={isActive}
                          title={section.description}
                        >
                          {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
                          <span>{section.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>

          <div className="settings-sidebar__footer">
            <div className="settings-sidebar__user">
              <p>
                {currentUserName}
              </p>
              <span>
                {currentUser?.email || currentUser?.role || 'VynDesk'}
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={handleLogout}
              className="settings-sidebar__logout"
            >
              <LogOut className="h-3.5 w-3.5 shrink-0" />
              {t('views:u.uLogout', 'Abmelden')}
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="settings-main">
          <div className="settings-main__inner">
            {/* Page Header */}
            <SettingsSectionHeader
              eyebrow="VynDesk Settings"
              title={activeSectionHeader.title}
              description={activeSectionHeader.description}
              icon={activeSectionHeader.icon}
              action={
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="settings-back-button"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('common:action.back', 'Zurück')}
                </Button>
              }
            />

            {/* Content based on active section */}
            <div className="settings-content">
            {activeSection === "users" && (
              <div className="space-y-6">
                {!isAdmin ? (
                  // Access denied for non-admin users
                  <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
                        <Shield className="h-8 w-8 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('common:state.accessDenied', 'Zugriff verweigert')}</h3>
                      <p className="text-sm text-slate-500 text-center max-w-md">
                        Sie haben keine Berechtigung, die Benutzerverwaltung anzuzeigen.
                        Diese Funktion ist nur für Administratoren verfügbar.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-slate-900">{t('settings:tbl.userMgmt', 'Benutzerverwaltung')}</h3>
                        <span className="text-xs text-slate-400 font-normal ml-2">({usersData?.data?.users?.length || 0} Nutzer)</span>
                      </div>
                      <p className="text-sm text-slate-500">
                        {t('settings:tbl.userMgmtHint', 'Verwalten Sie Benutzerkonten und weisen Sie Rollen zu.')}
                      </p>
                    </div>
                    <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 gap-2 text-slate-600 hover:text-slate-900 border-slate-200"
                        >
                          <Plus className="h-4 w-4" />
                          {t('settings:tbl.addUser', 'Benutzer hinzufügen')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
                        <div className="flex flex-1 overflow-hidden">
                          {/* Sidebar */}
                          <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-semibold text-slate-900">{t('settings:p2.newUser', 'Neuer Benutzer')}</span>
                              </div>
                              <p className="text-xs text-slate-500 leading-relaxed">
                                Erstellen Sie ein neues Benutzerkonto. Das System sendet automatisch eine Einladungs-E-Mail.
                              </p>
                            </div>
                            
                            <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                              <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">{t('settings:p2.roles', 'Rollen')}</span>
                              <ul className="space-y-2">
                                <li className="flex items-center gap-2 text-xs text-slate-600">
                                  <Crown className="h-3 w-3 text-amber-500" />
                                  <span>{t('settings:p2.adminFull', 'Admin: Voller Zugriff')}</span>
                                </li>
                                <li className="flex items-center gap-2 text-xs text-slate-600">
                                  <Headphones className="h-3 w-3 text-blue-500" />
                                  <span>{t('settings:p2.agentTickets', 'Agent: Ticket-Bearbeitung')}</span>
                                </li>
                              </ul>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex flex-col flex-1 bg-white min-w-0">
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                              <DialogTitle className="text-base font-semibold text-slate-900">
                                {t('common:x2.userDetails', 'Benutzerdetails')}
                              </DialogTitle>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-6">
                              <div className="space-y-6 max-w-lg">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label htmlFor="first-name" className="text-xs font-medium text-slate-700">{t('common:field.firstName', 'Vorname')}</Label>
                                    <Input
                                      id="first-name"
                                      placeholder="Max"
                                      value={newUser.firstName}
                                      onChange={(e) => handleUserInputChange('firstName', e.target.value)}
                                      className={`h-9 border-slate-200 focus-visible:ring-slate-200 ${validationErrors.firstName ? 'border-red-500' : ''}`}
                                    />
                                    {validationErrors.firstName && (
                                      <p className="text-xs text-red-600">{validationErrors.firstName}</p>
                                    )}
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="last-name" className="text-xs font-medium text-slate-700">{t('common:field.lastName', 'Nachname')}</Label>
                                    <Input
                                      id="last-name"
                                      placeholder="Mustermann"
                                      value={newUser.lastName}
                                      onChange={(e) => handleUserInputChange('lastName', e.target.value)}
                                      className={`h-9 border-slate-200 focus-visible:ring-slate-200 ${validationErrors.lastName ? 'border-red-500' : ''}`}
                                    />
                                    {validationErrors.lastName && (
                                      <p className="text-xs text-red-600">{validationErrors.lastName}</p>
                                    )}
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor="user-email" className="text-xs font-medium text-slate-700">{t('auth:register.email', 'E-Mail-Adresse')}</Label>
                                  <Input
                                    id="user-email"
                                    type="email"
                                    placeholder="max@unternehmen.de"
                                    value={newUser.email}
                                    onChange={(e) => handleUserInputChange('email', e.target.value)}
                                    className={`h-9 border-slate-200 focus-visible:ring-slate-200 ${validationErrors.email ? 'border-red-500' : ''}`}
                                  />
                                  {validationErrors.email && (
                                    <p className="text-xs text-red-600">{validationErrors.email}</p>
                                  )}
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor="user-role" className="text-xs font-medium text-slate-700">{t('common:field.role', 'Rolle')}</Label>
                                  <Select value={newUser.role} onValueChange={(value: 'admin' | 'agent' | 'warehouse') => {
                                    handleUserInputChange('role', value);
                                    // Clear organization validation error when role changes
                                    if (validationErrors.organizationIds) {
                                      setValidationErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors.organizationIds;
                                        return newErrors;
                                      });
                                    }
                                  }}>
                                    <SelectTrigger className="h-9 border-slate-200 focus:ring-slate-200">
                                      <SelectValue placeholder={t('settings:p2.phSelectRole', 'Rolle auswählen')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="admin">{t('common:role.admin', 'Administrator')}</SelectItem>
                                      <SelectItem value="agent">{t('common:role.agent', 'Support Agent')}</SelectItem>
                                      <SelectItem value="warehouse">{t('common:role.warehouse', 'Lager-Mitarbeiter')}</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Organization Selection - only for admin/agent */}
                                {(newUser.role === 'admin' || newUser.role === 'agent' || newUser.role === 'warehouse') && (
                                  <div className="space-y-2">
                                    <Label className="text-xs font-medium text-slate-700">{t('settings:p2.orgAccess', 'Organisationszugriff')}</Label>
                                    <div className={`border rounded-lg ${validationErrors.organizationIds ? 'border-red-500' : 'border-slate-200'}`}>
                                      <div className="p-3 border-b border-slate-100">
                                        <div className="flex items-center space-x-2">
                                          <Checkbox
                                            id="select-all-orgs"
                                            checked={selectAllOrgs}
                                            onCheckedChange={(checked) => {
                                              setSelectAllOrgs(!!checked);
                                              const allOrgIds = organizationsData?.data?.map((o: any) => o.id) || [];
                                              setNewUser(prev => ({
                                                ...prev,
                                                organizationIds: checked ? allOrgIds : []
                                              }));
                                              if (validationErrors.organizationIds) {
                                                setValidationErrors(prev => {
                                                  const newErrors = { ...prev };
                                                  delete newErrors.organizationIds;
                                                  return newErrors;
                                                });
                                              }
                                            }}
                                          />
                                          <label
                                            htmlFor="select-all-orgs"
                                            className="text-sm font-medium text-slate-700 cursor-pointer"
                                          >
                                            {t('common:x1.selectAllOrgs', 'Alle Organisationen auswählen')}
                                          </label>
                                        </div>
                                      </div>
                                      <div className="max-h-[180px] overflow-y-auto p-3 space-y-2">
                                        {organizationsData?.data?.map((org: any) => (
                                          <div key={org.id} className="flex items-center space-x-2">
                                            <Checkbox
                                              id={`org-${org.id}`}
                                              checked={newUser.organizationIds?.includes(org.id) || false}
                                              onCheckedChange={(checked) => {
                                                const currentIds = newUser.organizationIds || [];
                                                let newIds: string[];
                                                if (checked) {
                                                  newIds = [...currentIds, org.id];
                                                } else {
                                                  newIds = currentIds.filter(id => id !== org.id);
                                                }
                                                setNewUser(prev => ({
                                                  ...prev,
                                                  organizationIds: newIds
                                                }));
                                                // Update selectAll state
                                                const allOrgIds = organizationsData?.data?.map((o: any) => o.id) || [];
                                                setSelectAllOrgs(allOrgIds.every(id => newIds.includes(id)));
                                                if (validationErrors.organizationIds && newIds.length > 0) {
                                                  setValidationErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.organizationIds;
                                                    return newErrors;
                                                  });
                                                }
                                              }}
                                            />
                                            <label
                                              htmlFor={`org-${org.id}`}
                                              className="text-sm text-slate-600 cursor-pointer"
                                            >
                                              {org.name}
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    {validationErrors.organizationIds && (
                                      <p className="text-xs text-red-600">{validationErrors.organizationIds}</p>
                                    )}
                                  </div>
                                )}

                                <div className="space-y-2">
                                  <Label htmlFor="user-password" className="text-xs font-medium text-slate-700">{t('settings:p2.tempPassword', 'Temporäres Passwort')}</Label>
                                  <Input
                                    id="user-password"
                                    type="password"
                                    placeholder={t('settings:p2.phMin8', 'Mindestens 8 Zeichen')}
                                    value={newUser.password}
                                    onChange={(e) => handleUserInputChange('password', e.target.value)}
                                    className={`h-9 border-slate-200 focus-visible:ring-slate-200 ${validationErrors.password ? 'border-red-500' : ''}`}
                                  />
                                  
                                  {newUser.password && (
                                    <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                                      <p className="text-[10px] uppercase font-bold text-slate-400 mb-2">{t('settings:p2.requirements', 'Anforderungen')}</p>
                                      <div className="grid grid-cols-2 gap-2">
                                        {[
                                          { key: 'length', label: '8+ Zeichen', valid: validatePassword(newUser.password).requirements.length },
                                          { key: 'uppercase', label: 'Großbuchstabe', valid: validatePassword(newUser.password).requirements.uppercase },
                                          { key: 'lowercase', label: 'Kleinbuchstabe', valid: validatePassword(newUser.password).requirements.lowercase },
                                          { key: 'number', label: 'Zahl', valid: validatePassword(newUser.password).requirements.number },
                                          { key: 'special', label: 'Sonderzeichen', valid: validatePassword(newUser.password).requirements.special }
                                        ].map((req) => (
                                          <div key={req.key} className="flex items-center gap-1.5">
                                            {req.valid ? (
                                              <Check className="h-3 w-3 text-emerald-500" />
                                            ) : (
                                              <div className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                                            )}
                                            <span className={`text-xs ${req.valid ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                                              {req.label}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                onClick={handleCloseDialog}
                                className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                              >
                                {t('common:action.cancel', 'Abbrechen')}
                              </Button>
                              <Button
                                className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                                onClick={handleCreateUser}
                                disabled={
                                  createUserMutation.isPending ||
                                  !newUser.firstName.trim() ||
                                  !newUser.lastName.trim() ||
                                  !newUser.email.trim() ||
                                  !validatePassword(newUser.password).isValid ||
                                  ((newUser.role === 'admin' || newUser.role === 'agent' || newUser.role === 'warehouse') && (!newUser.organizationIds || newUser.organizationIds.length === 0))
                                }
                              >
                                {createUserMutation.isPending ? 'Wird erstellt...' : 'Benutzer erstellen'}
                              </Button>
                            </DialogFooter>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Edit User Dialog */}
                    <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
                      <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
                        <div className="flex flex-1 overflow-hidden">
                          {/* Sidebar */}
                          <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-semibold text-slate-900">{t('settings:p2.editUser', 'Benutzer bearbeiten')}</span>
                              </div>
                              <p className="text-xs text-slate-500 leading-relaxed">
                                Aktualisieren Sie die Profilinformationen oder ändern Sie den Status des Benutzers.
                              </p>
                            </div>
                            
                            {editingUser && (
                              <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                                <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">{t('settings:p2.lastLogin', 'Letzter Login')}</span>
                                <span className="text-xs text-slate-600 font-medium">
                                  {formatLastLogin(editingUser.lastLogin)}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex flex-col flex-1 bg-white min-w-0">
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                              <DialogTitle className="text-base font-semibold text-slate-900">
                                {t('common:x2.userProfile', 'Benutzerprofil')}
                              </DialogTitle>
                            </div>
                            
                            {editingUser && (
                              <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-6 max-w-lg">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-first-name" className="text-xs font-medium text-slate-700">{t('common:field.firstName', 'Vorname')}</Label>
                                      <Input
                                        id="edit-first-name"
                                        value={editingUser.firstName}
                                        onChange={(e) => setEditingUser({...editingUser, firstName: e.target.value})}
                                        className="h-9 border-slate-200 focus-visible:ring-slate-200"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-last-name" className="text-xs font-medium text-slate-700">{t('common:field.lastName', 'Nachname')}</Label>
                                      <Input
                                        id="edit-last-name"
                                        value={editingUser.lastName}
                                        onChange={(e) => setEditingUser({...editingUser, lastName: e.target.value})}
                                        className="h-9 border-slate-200 focus-visible:ring-slate-200"
                                      />
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <Label htmlFor="edit-email" className="text-xs font-medium text-slate-700">{t('common:field.email', 'E-Mail')}</Label>
                                    <Input
                                      id="edit-email"
                                      type="email"
                                      value={editingUser.email}
                                      onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                                      className="h-9 border-slate-200 focus-visible:ring-slate-200"
                                    />
                                  </div>

                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-role" className="text-xs font-medium text-slate-700">{t('common:field.role', 'Rolle')}</Label>
                                      <Select
                                        value={editingUser.role}
                                        onValueChange={(value: 'admin' | 'agent' | 'warehouse') =>
                                          setEditingUser({...editingUser, role: value})
                                        }
                                      >
                                        <SelectTrigger className="h-9 border-slate-200 focus:ring-slate-200">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="admin">{t('common:role.admin', 'Administrator')}</SelectItem>
                                          <SelectItem value="agent">{t('common:role.agent', 'Support Agent')}</SelectItem>
                                      <SelectItem value="warehouse">{t('common:role.warehouse', 'Lager-Mitarbeiter')}</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-status" className="text-xs font-medium text-slate-700">{t('common:field.status', 'Status')}</Label>
                                      <Select
                                        value={editingUser.status}
                                        onValueChange={(value: 'active' | 'inactive' | 'suspended') =>
                                          setEditingUser({...editingUser, status: value})
                                        }
                                      >
                                        <SelectTrigger className="h-9 border-slate-200 focus:ring-slate-200">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="active">{t('common:state.active', 'Aktiv')}</SelectItem>
                                          <SelectItem value="inactive">{t('common:state.inactive', 'Inaktiv')}</SelectItem>
                                          <SelectItem value="suspended">{t('common:state.blocked', 'Gesperrt')}</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>

                                  {/* Organization Selection - only for admin/agent */}
                                  {(editingUser.role === 'admin' || editingUser.role === 'agent' || editingUser.role === 'warehouse') && (
                                    <div className="space-y-2">
                                      <Label className="text-xs font-medium text-slate-700">{t('settings:p2.orgAccess', 'Organisationszugriff')}</Label>
                                      <div className="border rounded-lg border-slate-200">
                                        <div className="p-3 border-b border-slate-100">
                                          <div className="flex items-center space-x-2">
                                            <Checkbox
                                              id="edit-select-all-orgs"
                                              checked={editSelectAllOrgs}
                                              onCheckedChange={(checked) => {
                                                setEditSelectAllOrgs(!!checked);
                                                const allOrgIds = organizationsData?.data?.map((o: any) => o.id) || [];
                                                setEditingUser({
                                                  ...editingUser,
                                                  organizationIds: checked ? allOrgIds : []
                                                });
                                              }}
                                            />
                                            <label
                                              htmlFor="edit-select-all-orgs"
                                              className="text-sm font-medium text-slate-700 cursor-pointer"
                                            >
                                              {t('common:x1.selectAllOrgs', 'Alle Organisationen auswählen')}
                                            </label>
                                          </div>
                                        </div>
                                        <div className="max-h-[180px] overflow-y-auto p-3 space-y-2">
                                          {organizationsData?.data?.map((org: any) => (
                                            <div key={org.id} className="flex items-center space-x-2">
                                              <Checkbox
                                                id={`edit-org-${org.id}`}
                                                checked={editingUser.organizationIds?.includes(org.id) || false}
                                                onCheckedChange={(checked) => {
                                                  const currentIds = editingUser.organizationIds || [];
                                                  let newIds: string[];
                                                  if (checked) {
                                                    newIds = [...currentIds, org.id];
                                                  } else {
                                                    newIds = currentIds.filter(id => id !== org.id);
                                                  }
                                                  setEditingUser({
                                                    ...editingUser,
                                                    organizationIds: newIds
                                                  });
                                                  // Update selectAll state
                                                  const allOrgIds = organizationsData?.data?.map((o: any) => o.id) || [];
                                                  setEditSelectAllOrgs(allOrgIds.every(id => newIds.includes(id)));
                                                }}
                                              />
                                              <label
                                                htmlFor={`edit-org-${org.id}`}
                                                className="text-sm text-slate-600 cursor-pointer"
                                              >
                                                {org.name}
                                              </label>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      {(editingUser.role === 'admin' || editingUser.role === 'agent' || editingUser.role === 'warehouse') && (!editingUser.organizationIds || editingUser.organizationIds.length === 0) && (
                                        <p className="text-xs text-red-600">{t('settings:p2.orgRequired', 'Mindestens eine Organisation muss ausgewählt werden')}</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                onClick={() => setIsEditUserOpen(false)}
                                className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                              >
                                {t('common:action.cancel', 'Abbrechen')}
                              </Button>
                              <Button
                                className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                                onClick={handleUpdateUser}
                                disabled={
                                  updateUserMutation.isPending ||
                                  ((editingUser?.role === 'admin' || editingUser?.role === 'agent' || editingUser?.role === 'warehouse') && (!editingUser?.organizationIds || editingUser.organizationIds.length === 0))
                                }
                              >
                                {updateUserMutation.isPending ? 'Speichert...' : 'Änderungen speichern'}
                              </Button>
                            </DialogFooter>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    <UserDataTable
                      users={usersData?.data?.users || []}
                      isLoading={isLoadingUsers}
                      onEdit={handleEditUser}
                      onDeactivate={handleDeactivateUser}
                      isDeactivatePending={deactivateUserMutation.isPending}
                      statusFilter={userStatusFilter}
                      onStatusFilterChange={setUserStatusFilter}
                      onImpersonate={isAdmin ? handleImpersonate : undefined}
                      currentUserId={currentUser?.id}
                    />
                  </CardContent>
                </Card>
                )}
              </div>
            )}

            {activeSection === "roles" && (
              <div className="space-y-6">
                {/* Role Settings */}
                <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                  <CardHeader className="border-b border-slate-100 bg-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center text-lg">
                          <div className="w-10 h-10 bg-[#0f172a] rounded-lg flex items-center justify-center mr-3">
                            <Shield className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-bold text-gray-900">
                              {t('common:x2.roleSettings', 'Rolleneinstellungen')}
                            </div>
                            <CardDescription className="text-sm mt-1">
                              {t('common:x2.manageRolesDesc', 'Verwalten Sie Rollen und deren Berechtigungen')}
                            </CardDescription>
                          </div>
                        </CardTitle>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline">
                            <Plus className="h-4 w-4 mr-2" />
                            {t('common:x2.addRole', 'Rolle hinzufügen')}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{t('settings:p2.newRole', 'Neue Rolle erstellen')}</DialogTitle>
                            <DialogDescription>
                              Definieren Sie eine neue Rolle mit spezifischen
                              Berechtigungen.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="role-name">{t('settings:p2.roleName', 'Rollenname')}</Label>
                              <Input
                                id="role-name"
                                placeholder={t('settings:p2.phEgSeniorAgent', 'z.B. Senior Agent')}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>{t('common:field.permissions', 'Berechtigungen')}</Label>
                              <div className="grid grid-cols-2 gap-4">
                                {[
                                  {
                                    id: "tickets-view",
                                    label: "Tickets anzeigen",
                                  },
                                  {
                                    id: "tickets-create",
                                    label: "Tickets erstellen",
                                  },
                                  {
                                    id: "tickets-edit",
                                    label: "Tickets bearbeiten",
                                  },
                                  {
                                    id: "tickets-delete",
                                    label: "Tickets löschen",
                                  },
                                  {
                                    id: "tickets-assign",
                                    label: "Tickets zuweisen",
                                  },
                                  {
                                    id: "users-manage",
                                    label: "Benutzer verwalten",
                                  },
                                  {
                                    id: "settings-access",
                                    label: "Einstellungen zugreifen",
                                  },
                                  {
                                    id: "reports-view",
                                    label: "Berichte anzeigen",
                                  },
                                ].map((permission) => (
                                  <div
                                    key={permission.id}
                                    className="flex items-center space-x-2"
                                  >
                                    <Checkbox id={permission.id} />
                                    <Label
                                      htmlFor={permission.id}
                                      className="text-sm"
                                    >
                                      {permission.label}
                                    </Label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline">{t('common:action.cancel', 'Abbrechen')}</Button>
                            <Button className="bg-[#0F99FD] hover:bg-[#0c84e0] text-white transition-all duration-200">
                              {t('common:x2.createRole', 'Rolle erstellen')}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        {
                          name: t('common:role.admin', "Administrator"),
                          permissions: 8,
                          users: 1,
                          color: "bg-[#0F99FD]",
                        },
                        {
                          name: t('common:role.agent', "Support Agent"),
                          permissions: 4,
                          users: 3,
                          color: "bg-blue-500",
                        },
                        {
                          name: "Supervisor",
                          permissions: 6,
                          users: 1,
                          color: "bg-green-500",
                        },
                        {
                          name: "Betrachter",
                          permissions: 2,
                          users: 0,
                          color: "bg-gray-500",
                        },
                      ].map((role, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-3 h-3 rounded-full ${role.color}`}
                            ></div>
                            <div>
                              <p className="font-medium text-sm">{role.name}</p>
                              <p className="text-xs text-gray-500">
                                {role.permissions} Berechtigungen • {role.users}{" "}
                                Benutzer
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button variant="outline" size="sm">
                              <Edit className="h-3 w-3" />
                            </Button>
                            {role.users === 0 && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm">
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      {t('common:x2.deleteRole', 'Rolle löschen')}
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Sind Sie sicher, dass Sie die Rolle &quot;
                                      {role.name}&quot; löschen möchten?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>
                                      {t('common:action.cancel', 'Abbrechen')}
                                    </AlertDialogCancel>
                                    <AlertDialogAction className="bg-red-600 hover:bg-red-700">
                                      {t('common:action.delete', 'Löschen')}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === "snippets" && (
              <div className="space-y-6">
                <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-slate-900">{t('settings:p2.snippets', 'Textbausteine')}</h3>
                        <span className="text-xs text-slate-400 font-normal ml-2">({filteredSnippets.length} Vorlagen)</span>
                      </div>
                      <p className="text-sm text-slate-500">
                        Verwalten Sie wiederverwendbare Antworten und Shortcuts für schnellere Kommunikation.
                      </p>
                    </div>
                    <Dialog open={isAddSnippetOpen} onOpenChange={setIsAddSnippetOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 gap-2 text-slate-600 hover:text-slate-900 border-slate-200"
                        >
                          <Plus className="h-4 w-4" />
                          {t('common:x2.addSnippet', 'Textbaustein hinzufügen')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[900px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
                        <div className="flex flex-1 overflow-hidden">
                          {/* Sidebar */}
                          <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-semibold text-slate-900">{t('settings:p2.newSnippet', 'Neuer Textbaustein')}</span>
                              </div>
                              <p className="text-xs text-slate-500 leading-relaxed">
                                Erstellen Sie eine Vorlage mit Shortcut für den schnellen Zugriff im Editor.
                              </p>
                            </div>
                            
                            <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                              <span className="text-[10px] uppercase font-bold text-slate-400 block mb-2">{t('settings:p2.placeholders', 'Platzhalter')}</span>
                              <ul className="space-y-1.5">
                                <li className="text-[10px] text-slate-600 font-mono bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 truncate">
                                  {"#{ticket.customer.firstname}"}
                                </li>
                                <li className="text-[10px] text-slate-600 font-mono bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 truncate">
                                  {"#{order.number}"}
                                </li>
                                <li className="text-[10px] text-slate-600 font-mono bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 truncate">
                                  {"#{user.firstname}"}
                                </li>
                              </ul>
                              <p className="text-[10px] text-slate-400 mt-2">
                                Nutzen Sie den "Platzhalter"-Button im Editor, um alle verfügbaren Variablen einzufügen.
                              </p>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex flex-col flex-1 bg-white min-w-0">
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                              <DialogTitle className="text-base font-semibold text-slate-900">
                                {t('common:x2.detailsContent', 'Details & Inhalt')}
                              </DialogTitle>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-6">
                              <div className="space-y-6 max-w-2xl">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label htmlFor="snippet-name" className="text-xs font-medium text-slate-700">{t('common:field.name', 'Name')}</Label>
                                    <Input
                                      id="snippet-name"
                                      placeholder={t('settings:p2.phEgGreeting', 'z.B. Begrüßung')}
                                      value={newSnippet.name}
                                      onChange={(e) => setNewSnippet((prev) => ({ ...prev, name: e.target.value }))}
                                      className="h-9 border-slate-200 focus-visible:ring-slate-200"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="snippet-shortcut" className="text-xs font-medium text-slate-700">{t('settings:p2.shortcut', 'Shortcut')}</Label>
                                    <div className="relative">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">/</span>
                                      <Input
                                        id="snippet-shortcut"
                                        placeholder="hallo"
                                        value={newSnippet.shortcut.startsWith('/') ? newSnippet.shortcut.substring(1) : newSnippet.shortcut}
                                        onChange={(e) => setNewSnippet((prev) => ({ ...prev, shortcut: e.target.value.startsWith('/') ? e.target.value : `/${e.target.value}` }))}
                                        className="h-9 pl-6 border-slate-200 focus-visible:ring-slate-200 font-mono text-sm"
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label htmlFor="snippet-category" className="text-xs font-medium text-slate-700">{t('common:field.category', 'Kategorie')}</Label>
                                    <Select
                                      value={newSnippet.category}
                                      onValueChange={(value) => setNewSnippet((prev) => ({ ...prev, category: value }))}
                                    >
                                      <SelectTrigger id="snippet-category" className="h-9 border-slate-200 focus:ring-slate-200">
                                        <SelectValue placeholder={t('settings:p2.phSelectCategory', 'Kategorie auswählen')} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="general">{t('common:field.general', 'Allgemein')}</SelectItem>
                                        <SelectItem value="greeting">{t('common:field.greeting', 'Begrüßung')}</SelectItem>
                                        <SelectItem value="closing">{t('common:field.closing', 'Abschluss')}</SelectItem>
                                        <SelectItem value="shipping">{t('agent:dept.shipping', 'Versand')}</SelectItem>
                                        <SelectItem value="refund">{t('settings:p2.refund', 'Rückerstattung')}</SelectItem>
                                        <SelectItem value="support">{t('settings:p2.support', 'Support')}</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="snippet-description" className="text-xs font-medium text-slate-700">{t('common:field.descriptionOptional', 'Beschreibung (Optional)')}</Label>
                                    <Input
                                      id="snippet-description"
                                      placeholder={t('settings:p2.phUsedFor', 'Wofür wird das genutzt?')}
                                      value={newSnippet.description}
                                      onChange={(e) => setNewSnippet((prev) => ({ ...prev, description: e.target.value }))}
                                      className="h-9 border-slate-200 focus-visible:ring-slate-200"
                                    />
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <Label htmlFor="snippet-content" className="text-xs font-medium text-slate-700">{t('common:field.content', 'Inhalt')}</Label>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button variant="outline" size="sm" className="h-7 gap-1.5 text-[11px] text-slate-600 border-slate-200 hover:bg-slate-50">
                                          <Hash className="h-3 w-3" />
                                          {t('settings:p2.placeholders', 'Platzhalter')}
                                          <ChevronDown className="h-3 w-3 opacity-50" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent align="end" className="w-72 p-0" onWheel={(e) => e.stopPropagation()}>
                                        <div className="max-h-[320px] overflow-y-auto">
                                        {SNIPPET_PLACEHOLDER_GROUPS.map((group) => (
                                          <div key={group.label}>
                                            <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100 sticky top-0">
                                              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{group.label}</span>
                                            </div>
                                            {group.items.map((item) => (
                                              <button
                                                key={item.placeholder}
                                                type="button"
                                                className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 transition-colors text-left"
                                                onClick={() => {
                                                  setNewSnippet((prev) => ({
                                                    ...prev,
                                                    content: prev.content
                                                      ? `${prev.content}${item.placeholder}`
                                                      : item.placeholder,
                                                  }));
                                                }}
                                              >
                                                <span className="text-slate-600">{item.label}</span>
                                                <code className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">{item.placeholder}</code>
                                              </button>
                                            ))}
                                          </div>
                                        ))}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                  <div className="min-h-[200px] border border-slate-200 rounded-md overflow-hidden">
                                    <RichTextEditor
                                      value={newSnippet.content}
                                      onChange={(value) => setNewSnippet((prev) => ({ ...prev, content: value }))}
                                      placeholder="Hallo #{ticket.customer.firstname}, vielen Dank für Ihre Nachricht..."
                                    />
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label className="text-xs font-medium text-slate-700">{t('common:field.attachments', 'Dateianhänge')}</Label>
                                  <input
                                    type="file"
                                    multiple
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files.length > 0) {
                                        handleFileUpload(e.target.files);
                                      }
                                    }}
                                    className="hidden"
                                    id="snippet-file-upload"
                                    accept="image/*,application/pdf,.doc,.docx,.txt"
                                  />
                                  <div
                                    className="group relative flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/50 px-4 py-3 transition hover:border-slate-400 hover:bg-slate-50"
                                    onClick={() => document.getElementById('snippet-file-upload')?.click()}
                                  >
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white border border-slate-200">
                                      <Upload className="h-4 w-4 text-slate-500" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-slate-700">{t('settings:p2.attachFiles', 'Dateien anhängen')}</p>
                                      <p className="text-[10px] text-slate-400">{t('settings:p2.pdfImagesDocs', 'PDF, Bilder oder Dokumente')}</p>
                                    </div>
                                  </div>

                                  {(uploadingFiles.length > 0 || uploadedAttachments.length > 0) && (
                                    <div className="space-y-2 mt-2">
                                      {uploadingFiles.map((file, i) => (
                                        <div key={i} className="flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                          <span>{t('common:state.uploading', 'Wird hochgeladen...')}</span>
                                        </div>
                                      ))}
                                      {uploadedAttachments.map((attachment) => (
                                        <div key={attachment.id} className="flex items-center justify-between rounded-md border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 px-3 py-2">
                                          <div className="flex items-center gap-2 text-xs text-slate-600">
                                            <Paperclip className="h-3 w-3 text-slate-400" />
                                            <span className="truncate max-w-[200px]">{attachment.filename}</span>
                                          </div>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeAttachment(attachment.id)}
                                            className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                                          >
                                            <X className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                onClick={() => setIsAddSnippetOpen(false)}
                                className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                              >
                                {t('common:action.cancel', 'Abbrechen')}
                              </Button>
                              <Button 
                                className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                                onClick={handleCreateSnippet}
                                disabled={createSnippetMutation.isPending || !newSnippet.name || !newSnippet.shortcut || !newSnippet.content}
                              >
                                {createSnippetMutation.isPending ? 'Erstellt...' : 'Erstellen'}
                              </Button>
                            </DialogFooter>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Edit Snippet Dialog */}
                    {editingSnippet && (
                      <Dialog open={!!editingSnippet} onOpenChange={(open) => !open && setEditingSnippet(null)}>
                        <DialogContent className="sm:max-w-[900px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
                          <div className="flex flex-1 overflow-hidden">
                            {/* Sidebar */}
                            <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-semibold text-slate-900">{t('common:action.edit', 'Bearbeiten')}</span>
                                </div>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                  {t('common:x2.adjustSnippet', 'Passen Sie Inhalt oder Einstellungen dieses Textbausteins an.')}
                                </p>
                              </div>
                              
                              <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                                <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">{t('settings:p2.statistics', 'Statistik')}</span>
                                <div className="space-y-2 mt-2">
                                  <div className="flex justify-between text-xs">
                                    <span className="text-slate-500">{t('settings:p2.usage', 'Verwendung:')}</span>
                                    <span className="font-medium text-slate-900">{editingSnippet.usageCount || 0}x</span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-slate-500">{t('settings:p2.created', 'Erstellt:')}</span>
                                    <span className="font-medium text-slate-900">
                                      {formatDateFn(editingSnippet.createdAt)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Content */}
                            <div className="flex flex-col flex-1 bg-white min-w-0">
                              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                                <DialogTitle className="text-base font-semibold text-slate-900">
                                  {editingSnippet.name}
                                </DialogTitle>
                              </div>
                              
                              <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-6 max-w-2xl">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-snippet-name" className="text-xs font-medium text-slate-700">{t('common:field.name', 'Name')}</Label>
                                      <Input
                                        id="edit-snippet-name"
                                        value={editingSnippet.name}
                                        onChange={(e) => setEditingSnippet({ ...editingSnippet, name: e.target.value })}
                                        className="h-9 border-slate-200 focus-visible:ring-slate-200"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-snippet-shortcut" className="text-xs font-medium text-slate-700">{t('settings:p2.shortcut', 'Shortcut')}</Label>
                                      <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">/</span>
                                        <Input
                                          id="edit-snippet-shortcut"
                                          value={editingSnippet.shortcut.startsWith('/') ? editingSnippet.shortcut.substring(1) : editingSnippet.shortcut}
                                          onChange={(e) => setEditingSnippet({ ...editingSnippet, shortcut: e.target.value.startsWith('/') ? e.target.value : `/${e.target.value}` })}
                                          className="h-9 pl-6 border-slate-200 focus-visible:ring-slate-200 font-mono text-sm"
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-snippet-category" className="text-xs font-medium text-slate-700">{t('common:field.category', 'Kategorie')}</Label>
                                      <Select
                                        value={editingSnippet.category}
                                        onValueChange={(value) => setEditingSnippet({ ...editingSnippet, category: value })}
                                      >
                                        <SelectTrigger id="edit-snippet-category" className="h-9 border-slate-200 focus:ring-slate-200">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="general">{t('common:field.general', 'Allgemein')}</SelectItem>
                                          <SelectItem value="greeting">{t('common:field.greeting', 'Begrüßung')}</SelectItem>
                                          <SelectItem value="closing">{t('common:field.closing', 'Abschluss')}</SelectItem>
                                          <SelectItem value="shipping">{t('agent:dept.shipping', 'Versand')}</SelectItem>
                                          <SelectItem value="refund">{t('settings:p2.refund', 'Rückerstattung')}</SelectItem>
                                          <SelectItem value="support">{t('settings:p2.support', 'Support')}</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="edit-snippet-description" className="text-xs font-medium text-slate-700">{t('common:field.description', 'Beschreibung')}</Label>
                                      <Input
                                        id="edit-snippet-description"
                                        value={editingSnippet.description || ''}
                                        onChange={(e) => setEditingSnippet({ ...editingSnippet, description: e.target.value })}
                                        className="h-9 border-slate-200 focus-visible:ring-slate-200"
                                      />
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                      <Label htmlFor="edit-snippet-content" className="text-xs font-medium text-slate-700">{t('common:field.content', 'Inhalt')}</Label>
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <Button variant="outline" size="sm" className="h-7 gap-1.5 text-[11px] text-slate-600 border-slate-200 hover:bg-slate-50">
                                            <Hash className="h-3 w-3" />
                                            {t('settings:p2.placeholders', 'Platzhalter')}
                                            <ChevronDown className="h-3 w-3 opacity-50" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent align="end" className="w-72 p-0" onWheel={(e) => e.stopPropagation()}>
                                        <div className="max-h-[320px] overflow-y-auto">
                                          {SNIPPET_PLACEHOLDER_GROUPS.map((group) => (
                                            <div key={group.label}>
                                              <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100 sticky top-0">
                                                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{group.label}</span>
                                              </div>
                                              {group.items.map((item) => (
                                                <button
                                                  key={item.placeholder}
                                                  type="button"
                                                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 transition-colors text-left"
                                                  onClick={() => {
                                                    if (editingSnippet) {
                                                      setEditingSnippet({
                                                        ...editingSnippet,
                                                        content: editingSnippet.content
                                                          ? `${editingSnippet.content}${item.placeholder}`
                                                          : item.placeholder,
                                                      });
                                                    }
                                                  }}
                                                >
                                                  <span className="text-slate-600">{item.label}</span>
                                                  <code className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">{item.placeholder}</code>
                                                </button>
                                              ))}
                                            </div>
                                          ))}
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                    <div className="min-h-[200px] border border-slate-200 rounded-md overflow-hidden">
                                      <RichTextEditor
                                        value={editingSnippet.content}
                                        onChange={(value) => setEditingSnippet({ ...editingSnippet, content: value })}
                                      />
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <Label className="text-xs font-medium text-slate-700">{t('common:field.attachments', 'Dateianhänge')}</Label>
                                    <input
                                      type="file"
                                      multiple
                                      onChange={(e) => {
                                        if (e.target.files && e.target.files.length > 0) {
                                          handleEditFileUpload(e.target.files);
                                        }
                                      }}
                                      className="hidden"
                                      id="edit-snippet-file-upload"
                                      accept="image/*,application/pdf,.doc,.docx,.txt"
                                    />
                                    <div
                                      className="group relative flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/50 px-4 py-3 transition hover:border-slate-400 hover:bg-slate-50"
                                      onClick={() => document.getElementById('edit-snippet-file-upload')?.click()}
                                    >
                                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white border border-slate-200">
                                        <Upload className="h-4 w-4 text-slate-500" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-xs font-medium text-slate-700">{t('settings:p2.addFiles', 'Dateien hinzufügen')}</p>
                                        <p className="text-[10px] text-slate-400">{t('settings:p2.newFilesAppended', 'Neue Dateien werden der Liste hinzugefügt')}</p>
                                      </div>
                                    </div>

                                    {(uploadingFiles.length > 0 || editUploadedAttachments.length > 0 || uploadedAttachments.length > 0) && (
                                      <div className="space-y-2 mt-2">
                                        {uploadingFiles.map((file, i) => (
                                          <div key={i} className="flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                            <span>{t('common:state.uploading', 'Wird hochgeladen...')}</span>
                                          </div>
                                        ))}

                                        {/* Existing attachments from the snippet */}
                                        {editUploadedAttachments.map((attachment: any) => (
                                          <div key={attachment.id} className="flex items-center justify-between rounded-md border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 px-3 py-2">
                                            <div className="flex items-center gap-2 text-xs text-slate-600">
                                              <Paperclip className="h-3 w-3 text-slate-400" />
                                              <span className="truncate max-w-[200px]">{attachment.filename}</span>
                                            </div>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => {
                                                // Remove from editUploadedAttachments and update editingSnippet.attachmentIds
                                                setEditUploadedAttachments(prev => prev.filter(a => a.id !== attachment.id));
                                                if (editingSnippet) {
                                                  setEditingSnippet({
                                                    ...editingSnippet,
                                                    attachmentIds: editingSnippet.attachmentIds?.filter(id => id !== attachment.id) || []
                                                  });
                                                }
                                              }}
                                              className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                                            >
                                              <X className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        ))}

                                        {/* Newly uploaded attachments in this session */}
                                        {uploadedAttachments.map((attachment) => (
                                          <div key={attachment.id} className="flex items-center justify-between rounded-md border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 px-3 py-2">
                                            <div className="flex items-center gap-2 text-xs text-slate-600">
                                              <Paperclip className="h-3 w-3 text-slate-400" />
                                              <span className="truncate max-w-[200px]">{attachment.filename}</span>
                                              <Badge variant="secondary" className="text-[10px] h-4 px-1">{t('domain:status.new', 'Neu')}</Badge>
                                            </div>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => removeAttachment(attachment.id)}
                                              className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                                            >
                                              <X className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setEditingSnippet(null);
                                    setUploadedAttachments([]); // Clear new uploads on cancel
                                  }}
                                  className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                                >
                                  {t('common:action.cancel', 'Abbrechen')}
                                </Button>
                                <Button 
                                  className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                                  onClick={handleUpdateSnippet}
                                  disabled={updateSnippetMutation.isPending}
                                >
                                  {updateSnippetMutation.isPending ? 'Speichert...' : 'Änderungen speichern'}
                                </Button>
                              </DialogFooter>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </CardHeader>
                  <CardContent>
                    <SnippetDataTable 
                      snippets={snippets}
                      isLoading={isLoadingSnippets}
                      onEdit={handleEditSnippet}
                      onDelete={handleDeleteSnippet}
                      isDeletePending={deleteSnippetMutation.isPending}
                    />
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === "organizations" && <OrganizationsSection />}
            {activeSection === "lager" && <WarehouseSettingsSection />}
            {activeSection === "language" && <LanguageSection />}
            {activeSection === "mobile-app" && <MobileAppSection />}
            {activeSection === "mcp" && <McpSection />}
            {activeSection === "voice" && <VoiceSection />}
            {activeSection === "whatsapp" && <WhatsappSection />}
            {activeSection === "meta-messaging" && <MetaMessagingSection />}

            {activeSection === "sla" && <SLASettings />}

            {activeSection === "ai-training" && <AITrainingManagement />}

            {activeSection === "plugins" && <PluginsTab />}

            {activeSection === "tags" && <TagsTab />}

            {activeSection === "tickets" && (
              <div className="space-y-6">
                {/* Ticket Number Settings */}
                <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                  <CardHeader className="border-b border-slate-100 bg-white">
                    <CardTitle className="flex items-center text-lg">
                      <div className="w-10 h-10 bg-[#0f172a] rounded-lg flex items-center justify-center mr-3">
                        <Hash className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">
                          {t('common:x2.ticketNumSettings', 'Ticketnummer Einstellungen')}
                        </div>
                        <CardDescription className="text-sm mt-1">
                          Konfigurieren Sie das Format und die Nummerierung
                          Ihrer Tickets
                        </CardDescription>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="ticket-hook">
                            {t('common:x2.ticketHookPrefix', 'Ticket Hook (Präfix)')}
                          </Label>
                          <Input
                            id="ticket-hook"
                            defaultValue="SUP"
                            placeholder="SUP"
                          />
                          <p className="text-xs text-gray-500">
                            {t('common:x2.prefixForAll', 'Präfix für alle Ticketnummern (z.B. SUP-001)')}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ticket-hook-position">
                            {t('common:x2.hookPosition', 'Hook Position')}
                          </Label>
                          <Select defaultValue="left">
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="left">
                                {t('common:x2.leftSup', 'Links (SUP-001)')}
                              </SelectItem>
                              <SelectItem value="right">
                                {t('common:x2.rightSup', 'Rechts (001-SUP)')}
                              </SelectItem>
                              <SelectItem value="none">{t('settings:p2.prefixNone', 'Keine (001)')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="ticket-counter">
                            {t('common:x2.currentTicketNum', 'Aktuelle Ticketnummer')}
                          </Label>
                          <Input
                            id="ticket-counter"
                            type="number"
                            defaultValue="1247"
                          />
                          <p className="text-xs text-gray-500">
                            Die nächste Ticketnummer wird 1248 sein
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ticket-min-length">
                            {t('common:x2.minLength', 'Minimale Länge')}
                          </Label>
                          <Select defaultValue="3">
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 (1, 2, 3...)</SelectItem>
                              <SelectItem value="2">
                                2 (01, 02, 03...)
                              </SelectItem>
                              <SelectItem value="3">
                                3 (001, 002, 003...)
                              </SelectItem>
                              <SelectItem value="4">
                                4 (0001, 0002, 0003...)
                              </SelectItem>
                              <SelectItem value="5">
                                5 (00001, 00002, 00003...)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-sm mb-2">{t('common:field.preview', 'Vorschau')}</h4>
                      <p className="text-sm text-gray-600">
                        {t('common:x2.nextTicketNum', 'Nächste Ticketnummer:')}{" "}
                        <span className="font-mono bg-white px-2 py-1 rounded border">
                          SUP-1248
                        </span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === "email" && (
              <div className="space-y-6">
                {/* Email Settings */}
                <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                  <CardHeader className="border-b border-slate-100 bg-white">
                    <CardTitle className="flex items-center text-lg">
                      <div className="w-10 h-10 bg-[#0f172a] rounded-lg flex items-center justify-center mr-3">
                        <Mail className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">
                          E-Mail Einstellungen
                        </div>
                        <CardDescription className="text-sm mt-1">
                          {t('common:x2.configEmailPerShop', 'Konfigurieren Sie E-Mail-Einstellungen pro Shop')}
                        </CardDescription>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="email-shop-select">{t('settings:p2.selectShop', 'Shop auswählen')}</Label>
                      <Select defaultValue="hauptshop">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hauptshop">Hauptshop</SelectItem>
                          <SelectItem value="b2b">B2B Portal</SelectItem>
                          <SelectItem value="outlet">Outlet Store</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                      {/* Incoming Mail (IMAP) */}
                      <div className="space-y-4">
                        <h4 className="font-medium flex items-center">
                          <Server className="h-4 w-4 mr-2" />
                          {t('common:x2.incomingImap', 'Eingehende E-Mails (IMAP)')}
                        </h4>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="imap-server">{t('settings:p2.imapServer', 'IMAP Server')}</Label>
                            <Input
                              id="imap-server"
                              defaultValue="imap.gmail.com"
                              placeholder="imap.provider.de"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label htmlFor="imap-port">{t('settings:p2.port', 'Port')}</Label>
                              <Input
                                id="imap-port"
                                defaultValue="993"
                                placeholder="993"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="imap-security">
                                {t('common:x1.encryption', 'Verschlüsselung')}
                              </Label>
                              <Select defaultValue="ssl">
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ssl">{t('settings:p2.ssltls', 'SSL/TLS')}</SelectItem>
                                  <SelectItem value="starttls">
                                    STARTTLS
                                  </SelectItem>
                                  <SelectItem value="none">{t('common:state.none', 'Keine')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="imap-username">{t('common:field.username', 'Benutzername')}</Label>
                            <Input
                              id="imap-username"
                              defaultValue="support@hauptshop.de"
                              placeholder="support@shop.de"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="imap-password">{t('common:field.password', 'Passwort')}</Label>
                            <Input
                              id="imap-password"
                              type="password"
                              defaultValue="••••••••"
                              placeholder={t('common:field.password', 'Passwort')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="imap-folder">{t('settings:p2.mailbox', 'Postfach')}</Label>
                            <Input
                              id="imap-folder"
                              defaultValue="INBOX"
                              placeholder="INBOX"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Outgoing Mail (SMTP) */}
                      <div className="space-y-4">
                        <h4 className="font-medium flex items-center">
                          <Server className="h-4 w-4 mr-2" />
                          {t('common:x2.outgoingSmtp', 'Ausgehende E-Mails (SMTP)')}
                        </h4>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="smtp-server">{t('settings:p2.smtpServer', 'SMTP Server')}</Label>
                            <Input
                              id="smtp-server"
                              defaultValue="smtp.gmail.com"
                              placeholder="smtp.provider.de"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label htmlFor="smtp-port">{t('settings:p2.port', 'Port')}</Label>
                              <Input
                                id="smtp-port"
                                defaultValue="587"
                                placeholder="587"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="smtp-security">
                                {t('common:x1.encryption', 'Verschlüsselung')}
                              </Label>
                              <Select defaultValue="tls">
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="tls">TLS</SelectItem>
                                  <SelectItem value="ssl">SSL</SelectItem>
                                  <SelectItem value="starttls">
                                    STARTTLS
                                  </SelectItem>
                                  <SelectItem value="none">{t('common:state.none', 'Keine')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="smtp-username">{t('common:field.username', 'Benutzername')}</Label>
                            <Input
                              id="smtp-username"
                              defaultValue="support@hauptshop.de"
                              placeholder="support@shop.de"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="smtp-password">{t('common:field.password', 'Passwort')}</Label>
                            <Input
                              id="smtp-password"
                              type="password"
                              defaultValue="••••••••"
                              placeholder={t('common:field.password', 'Passwort')}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label>{t('settings:p2.smtpAuth', 'SMTP Authentifizierung')}</Label>
                              <p className="text-sm text-gray-500">
                                {t('common:x2.authRequired', 'Authentifizierung erforderlich')}
                              </p>
                            </div>
                            <Switch defaultChecked />
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                      {/* Email Settings */}
                      <div className="space-y-4">
                        <h4 className="font-medium">E-Mail Einstellungen</h4>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="email-from-name">
                              {t('common:x2.senderName', 'Absendername')}
                            </Label>
                            <Input
                              id="email-from-name"
                              defaultValue="Hauptshop Support"
                              placeholder={t('settings:p2.phYourSupportTeam', 'Ihr Support Team')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email-from-address">
                              {t('views:f.fSenderEmail', 'Absender E-Mail')}
                            </Label>
                            <Input
                              id="email-from-address"
                              defaultValue="support@hauptshop.de"
                              placeholder="support@shop.de"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email-subject-prefix">
                              {t('common:x2.subjectPrefix', 'Betreff Präfix')}
                            </Label>
                            <Input
                              id="email-subject-prefix"
                              defaultValue="[Hauptshop Support]"
                              placeholder="[Support]"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email-reply-to">{t('settings:p2.replyTo', 'Antwort an')}</Label>
                            <Input
                              id="email-reply-to"
                              defaultValue="support@hauptshop.de"
                              placeholder="support@shop.de"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Email Signature */}
                      <div className="space-y-4">
                        <h4 className="font-medium">E-Mail Signatur</h4>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="email-signature">{t('settings:p2.signature', 'Signatur')}</Label>
                            <Textarea
                              id="email-signature"
                              rows={6}
                              defaultValue={`Mit freundlichen Grüßen
Ihr Hauptshop Support Team

Hauptshop GmbH
Musterstraße 123
12345 Musterstadt
Tel: +49 123 456789
E-Mail: support@hauptshop.de`}
                              placeholder={t('settings:p2.phSignature', 'Ihre E-Mail Signatur...')}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label>{t('settings:p2.htmlSignature', 'HTML Signatur')}</Label>
                              <p className="text-sm text-gray-500">
                                {t('common:x2.useAdvancedFormatting', 'Erweiterte Formatierung verwenden')}
                              </p>
                            </div>
                            <Switch />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button variant="outline">
                        <TestTube className="h-4 w-4 mr-2" />
                        {t('common:x2.testConnection', 'Verbindung testen')}
                      </Button>
                      <Button className="bg-[#0F99FD] hover:bg-[#0c84e0] text-white transition-all duration-200">
                        <Save className="h-4 w-4 mr-2" />
                        {t('common:x2.saveSettings', 'Einstellungen speichern')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === "integrations" && (
              <div className="space-y-5">
                <ShopIntegrationsSection />
                <PaymentsSection />
                <LexofficeSection />
              </div>
            )}

            {activeSection === "signatures" && (
              <SignatureSection />
            )}

            {activeSection === "auto-replies" && (
              <div className="space-y-6">
                <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-slate-900">{t('settings:p2.autoReplies', 'Auto-Antworten')}</h3>
                        <span className="text-xs text-slate-400 font-normal ml-2">({autoReplies.length} Konfigurationen)</span>
                      </div>
                      <p className="text-sm text-slate-500">
                        {t('common:x2.manageAutoConfirm', 'Automatische Eingangsbestätigungen pro Organisation verwalten.')}
                      </p>
                    </div>
                    <Dialog open={isAddAutoReplyOpen} onOpenChange={setIsAddAutoReplyOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 gap-2 text-slate-600 hover:text-slate-900 border-slate-200"
                          onClick={() => {
                            setEditingAutoReply(null);
                            setNewAutoReply({ organizationId: '', templateHtml: '', isActive: true });
                          }}
                          disabled={availableOrgs.length === 0}
                        >
                          <Plus className="h-4 w-4" />
                          {t('common:x2.addAutoReply', 'Auto-Antwort hinzufügen')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
                        <div className="flex flex-1 overflow-hidden">
                          {/* Sidebar */}
                          <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-semibold text-slate-900">{t('settings:p2.configuration', 'Konfiguration')}</span>
                              </div>
                              <p className="text-xs text-slate-500 leading-relaxed">
                                Erstellen Sie eine automatische Antwort für neu eingehende Tickets dieser Organisation.
                              </p>
                            </div>
                            
                            <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                              <span className="text-[10px] uppercase font-bold text-slate-400 block mb-2">{t('settings:p2.importantNotes', 'Wichtige Hinweise')}</span>
                              <ul className="space-y-2">
                                <li className="flex items-start gap-2 text-xs text-slate-600">
                                  <div className="h-1.5 w-1.5 rounded-full bg-slate-400 mt-1.5 flex-shrink-0" />
                                  <span>{t('settings:p2.ticketNumAppended', 'Ticketnummer wird automatisch angehängt')}</span>
                                </li>
                                <li className="flex items-start gap-2 text-xs text-slate-600">
                                  <div className="h-1.5 w-1.5 rounded-full bg-slate-400 mt-1.5 flex-shrink-0" />
                                  <span>{t('settings:p2.oncePerTicket', 'Wird nur einmal pro Ticket gesendet')}</span>
                                </li>
                                <li className="flex items-start gap-2 text-xs text-slate-600">
                                  <div className="h-1.5 w-1.5 rounded-full bg-slate-400 mt-1.5 flex-shrink-0" />
                                  <span>{t('settings:p2.htmlSupported', 'HTML-Formatierung wird unterstützt')}</span>
                                </li>
                              </ul>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex flex-col flex-1 bg-white min-w-0">
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                              <DialogTitle className="text-base font-semibold text-slate-900">
                                {t('common:x2.createAutoReply', 'Auto-Antwort erstellen')}
                              </DialogTitle>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto p-6">
                              <div className="space-y-6 max-w-2xl">
                                <div className="space-y-2">
                                  <Label htmlFor="ar-org" className="text-xs font-medium text-slate-700">{t('common:field.organization', 'Organisation')}</Label>
                                  <Select
                                    value={newAutoReply.organizationId}
                                    onValueChange={(value) => setNewAutoReply(prev => ({ ...prev, organizationId: value }))}
                                  >
                                    <SelectTrigger id="ar-org" className="h-9 border-slate-200 focus:ring-slate-200">
                                      <SelectValue placeholder={t('settings:p2.phSelectOrg', 'Organisation auswählen')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {availableOrgs.map(org => (
                                        <SelectItem key={org.id} value={org.id}>
                                          {org.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {availableOrgs.length === 0 && (
                                    <p className="text-[10px] text-amber-600">
                                      {t('common:x2.allOrgsConfigured', 'Alle Organisationen haben bereits eine Konfiguration.')}
                                    </p>
                                  )}
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor="ar-template" className="text-xs font-medium text-slate-700">{t('settings:p2.htmlTemplate', 'HTML Vorlage')}</Label>
                                  <div className="min-h-[200px] border border-slate-200 rounded-md overflow-hidden bg-slate-50">
                                    <textarea
                                      id="ar-template"
                                      className="w-full h-[250px] p-4 bg-slate-50 text-xs font-mono resize-none focus:outline-none"
                                      placeholder="<div>Guten Tag,<br><br>vielen Dank für Ihre Anfrage...</div>"
                                      value={newAutoReply.templateHtml}
                                      onChange={(e) => setNewAutoReply(prev => ({ ...prev, templateHtml: e.target.value }))}
                                    />
                                  </div>
                                  <p className="text-[10px] text-slate-400">
                                    {t('common:x2.useStandardHtml', 'Verwenden Sie Standard HTML-Tags für die Formatierung.')}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                onClick={() => setIsAddAutoReplyOpen(false)}
                                className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                              >
                                {t('common:action.cancel', 'Abbrechen')}
                              </Button>
                              <Button 
                                className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                                onClick={handleSaveAutoReply}
                                disabled={upsertAutoReplyMutation.isPending || !newAutoReply.organizationId || !newAutoReply.templateHtml}
                              >
                                {upsertAutoReplyMutation.isPending ? 'Wird erstellt...' : 'Erstellen'}
                              </Button>
                            </DialogFooter>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Edit Auto-Reply Dialog */}
                    {editingAutoReply && (
                      <Dialog open={!!editingAutoReply} onOpenChange={(open) => !open && setEditingAutoReply(null)}>
                        <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
                          <div className="flex flex-1 overflow-hidden">
                            {/* Sidebar */}
                            <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-semibold text-slate-900">{t('common:action.edit', 'Bearbeiten')}</span>
                                </div>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                  {t('common:x2.adjustAutoReplyFor', 'Passen Sie die automatische Antwort für')} {editingAutoReply.organization_name || getOrgName(editingAutoReply.organization_id)} an.
                                </p>
                              </div>
                              
                              <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                                <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">{t('common:field.status', 'Status')}</span>
                                <div className="flex items-center gap-2 mt-2">
                                  <div className={`h-2 w-2 rounded-full ${editingAutoReply.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                  <span className="text-xs font-medium text-slate-700">
                                    {editingAutoReply.is_active ? 'Aktiviert' : 'Deaktiviert'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Content */}
                            <div className="flex flex-col flex-1 bg-white min-w-0">
                              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                                <DialogTitle className="text-base font-semibold text-slate-900">
                                  {t('common:x2.editContent', 'Inhalt bearbeiten')}
                                </DialogTitle>
                              </div>
                              
                              <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-6 max-w-2xl">
                                  <div className="space-y-2">
                                    <Label className="text-xs font-medium text-slate-700">{t('common:field.organization', 'Organisation')}</Label>
                                    <div className="px-3 py-2 bg-slate-50 rounded-md border border-slate-200 text-sm text-slate-600">
                                      {editingAutoReply.organization_name || getOrgName(editingAutoReply.organization_id)}
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <Label htmlFor="edit-ar-template" className="text-xs font-medium text-slate-700">{t('settings:p2.htmlTemplate', 'HTML Vorlage')}</Label>
                                    <div className="min-h-[200px] border border-slate-200 rounded-md overflow-hidden bg-slate-50">
                                      <textarea
                                        id="edit-ar-template"
                                        className="w-full h-[250px] p-4 bg-slate-50 text-xs font-mono resize-none focus:outline-none"
                                        value={editingAutoReply.template_html}
                                        onChange={(e) => setEditingAutoReply({ ...editingAutoReply, template_html: e.target.value })}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                                <Button
                                  variant="outline"
                                  onClick={() => setEditingAutoReply(null)}
                                  className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                                >
                                  {t('common:action.cancel', 'Abbrechen')}
                                </Button>
                                <Button 
                                  className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                                  onClick={handleSaveAutoReply}
                                  disabled={upsertAutoReplyMutation.isPending || !editingAutoReply.template_html}
                                >
                                  {upsertAutoReplyMutation.isPending ? 'Speichert...' : 'Änderungen speichern'}
                                </Button>
                              </DialogFooter>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </CardHeader>
                  <CardContent>
                    <AutoReplyDataTable 
                      autoReplies={autoReplies}
                      organizations={organizationsData?.data || []}
                      isLoading={isLoadingAutoReplies}
                      onEdit={handleEditAutoReply}
                      onDelete={handleDeleteAutoReply}
                      onToggleStatus={handleToggleAutoReply}
                    />
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === "contacts" && (
              <FrequentContactsSection />
            )}

            {activeSection === "developer" && (
              <DeveloperSection />
            )}

            {activeSection === "license" && (
              <LicenseSection />
            )}

            {activeSection === "chatbot" && <ChatbotSection />}

            {activeSection === "notification-recipients" && (
              <div className="space-y-6">
                <EmailSubscriptionsCard />
              </div>
            )}

            {activeSection === "notifications" && (
              <div className="space-y-6">
                {/* Notifications */}
                <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                  <CardHeader className="border-b border-slate-100 bg-white">
                    <CardTitle className="flex items-center text-lg">
                      <div className="w-10 h-10 bg-[#0f172a] rounded-lg flex items-center justify-center mr-3">
                        <Bell className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">
                          {t('common:x2.notificationSettings', 'Benachrichtigungseinstellungen')}
                        </div>
                        <CardDescription className="text-sm mt-1">
                          Konfigurieren Sie, wann und wie Sie benachrichtigt
                          werden möchten
                        </CardDescription>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>E-Mail-Benachrichtigungen</Label>
                          <p className="text-sm text-gray-500">
                            {t('common:x2.emailOnNewTickets', 'Erhalten Sie E-Mails bei neuen Tickets und Updates')}
                          </p>
                        </div>
                        <Switch
                          checked={emailNotifications}
                          onCheckedChange={setEmailNotifications}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>{t('settings:p2.slaWarnings', 'SLA-Warnungen')}</Label>
                          <p className="text-sm text-gray-500">
                            {t('common:x2.notifySlaBreach', 'Benachrichtigungen bei drohenden SLA-Verletzungen')}
                          </p>
                        </div>
                        <Switch
                          checked={slaAlerts}
                          onCheckedChange={setSlaAlerts}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>{t('settings:p2.desktopNotif', 'Desktop-Benachrichtigungen')}</Label>
                          <p className="text-sm text-gray-500">
                            {t('common:x2.browserNotifUrgent', 'Browser-Benachrichtigungen für dringende Tickets')}
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>{t('settings:p2.dailySummary', 'Tägliche Zusammenfassung')}</Label>
                          <p className="text-sm text-gray-500">
                            {t('common:x2.dailyEmailReport', 'Täglicher E-Mail-Bericht über Ticket-Status')}
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <h4 className="font-medium">E-Mail-Vorlagen</h4>
                      <div className="space-y-2">
                        <Label htmlFor="welcome-template">
                          {t('common:x2.welcomeEmail', 'Willkommens-E-Mail')}
                        </Label>
                        <Textarea
                          id="welcome-template"
                          placeholder={t('settings:p2.phWelcomeTemplate', 'Ihre Willkommens-E-Mail Vorlage...')}
                          defaultValue="Vielen Dank für Ihre Anfrage. Wir haben Ihr Ticket erhalten und werden uns schnellstmöglich bei Ihnen melden."
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="closing-template">
                          {t('common:x2.closingEmail', 'Abschluss-E-Mail')}
                        </Label>
                        <Textarea
                          id="closing-template"
                          placeholder={t('settings:p2.phClosingTemplate', 'Ihre Abschluss-E-Mail Vorlage...')}
                          defaultValue="Ihr Ticket wurde erfolgreich bearbeitet. Falls Sie weitere Fragen haben, können Sie gerne ein neues Ticket erstellen."
                          rows={3}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === "general" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Company Information */}
                  <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                    <CardHeader className="border-b border-slate-100 bg-white">
                      <CardTitle className="flex items-center text-lg">
                        <div className="w-10 h-10 bg-[#0f172a] rounded-lg flex items-center justify-center mr-3">
                          <Shield className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">
                            {t('common:x2.companyInfo', 'Unternehmensinformationen')}
                          </div>
                          <CardDescription className="text-sm mt-1">
                            {t('common:x2.basicCompanyInfo', 'Grundlegende Informationen über Ihr Unternehmen')}
                          </CardDescription>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="company-name">{t('settings:p2.companyName', 'Firmenname')}</Label>
                        <Input
                          id="company-name"
                          defaultValue="Muster GmbH"
                          placeholder={t('settings:p2.phYourCompany', 'Ihr Firmenname')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company-email">{t('auth:register.email', 'E-Mail-Adresse')}</Label>
                        <Input
                          id="company-email"
                          type="email"
                          defaultValue="support@muster-gmbh.de"
                          placeholder="support@unternehmen.de"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company-phone">{t('settings:p2.phoneNumber', 'Telefonnummer')}</Label>
                        <Input
                          id="company-phone"
                          defaultValue="+49 123 456789"
                          placeholder="+49 123 456789"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="timezone">{t('settings:p2.timezone', 'Zeitzone')}</Label>
                        <Select defaultValue="europe-berlin">
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="europe-berlin">
                              Europa/Berlin
                            </SelectItem>
                            <SelectItem value="europe-vienna">
                              Europa/Wien
                            </SelectItem>
                            <SelectItem value="europe-zurich">
                              Europa/Zürich
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>

                  {/* System Configuration */}
                  <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
                    <CardHeader className="border-b border-slate-100 bg-white">
                      <CardTitle className="flex items-center text-lg">
                        <div className="w-10 h-10 bg-[#0f172a] rounded-lg flex items-center justify-center mr-3">
                          <Database className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">
                            {t('settings:p2.systemConfig', 'Systemkonfiguration')}
                          </div>
                          <CardDescription className="text-sm mt-1">
                            {t('common:x2.generalSystemSettings', 'Allgemeine Systemeinstellungen')}
                          </CardDescription>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="default-language">
                          {t('common:x2.defaultLanguage', 'Standardsprache')}
                        </Label>
                        <Select defaultValue="de">
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="de">Deutsch</SelectItem>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="fr">Français</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ticket-prefix">{t('settings:p2.ticketPrefix', 'Ticket-Präfix')}</Label>
                        <Input
                          id="ticket-prefix"
                          defaultValue="SUP"
                          placeholder="SUP"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sla-hours">
                          {t('common:x2.defaultSlaHours', 'Standard SLA (Stunden)')}
                        </Label>
                        <Input
                          id="sla-hours"
                          type="number"
                          defaultValue="24"
                          placeholder="24"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>{t('settings:p2.enableAutoAssign', 'Auto-Assignment aktivieren')}</Label>
                          <p className="text-sm text-gray-500">
                            {t('common:x2.autoAssignTickets', 'Tickets automatisch zuweisen')}
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="flex justify-center">
                  <Button className="bg-[#0F99FD] hover:bg-[#0c84e0] text-white px-8 py-3 rounded-xl transition-all duration-300">
                    <Save className="h-5 w-5 mr-2" />
                    {t('common:x2.saveAllSettings', 'Alle Einstellungen speichern')}
                  </Button>
                </div>
              </div>
            )}
            </div>
          </div>
        </main>
      </div>

      {/* File Preview Modal - Images & PDFs */}
      <Dialog open={filePreviewOpen} onOpenChange={setFilePreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {previewFileType?.startsWith('image/') ? 'Bild Vorschau' : 'Datei Vorschau'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            {previewFileType?.startsWith('image/') ? (
              <img
                src={previewFileUrl}
                alt="Preview"
                className="max-w-full max-h-[70vh] object-contain"
                onError={() => setFilePreviewOpen(false)}
              />
            ) : previewFileType === 'application/pdf' ? (
              <iframe
                src={previewFileUrl}
                className="w-full h-[70vh] border-0"
                title={t('ticket:section.pdfPreview', 'PDF Preview')}
                onError={() => setFilePreviewOpen(false)}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>{t('settings:p2.noPreviewType', 'Vorschau für diesen Dateityp nicht verfügbar')}</p>
                <button
                  onClick={() => window.open(previewFileUrl, '_blank')}
                  className="mt-4 text-blue-600 hover:text-blue-800 underline"
                >
                  {t('common:x2.openFileNewTab', 'Datei in neuem Tab öffnen ↗')}
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default SettingsPage;
